import { Link, NavLink } from 'react-router-dom';
import { PanelLeftOpenIcon } from 'lucide-react';

import { appIdentity } from '@/app/app-config';
import { AssistantLauncher } from '@/components/assistant/assistant-launcher';
import { TenantContextIndicator } from '@/components/tenancy/tenant-context-indicator';
import { useCustomerSkin } from '@/components/tenancy/customer-brand-provider';
import { NotificationBell } from '@/components/layout/notification-bell';
import { ProfileMenu } from '@/components/layout/profile-menu';
import { Button } from '@/components/ui/button';
import { useAppRoutes } from '@/hooks/use-app-routes';
import { createTopNavigation } from '@/lib/navigation';
import { cn } from '@/lib/utils';

function TopNav() {
  const routes = useAppRoutes();
  const navigation = createTopNavigation(routes);
  const { header } = useCustomerSkin();

  if (navigation.length === 0) return null;

  return (
    <nav
      aria-label="Primary"
      className={cn(
        'hidden items-center gap-0.5 rounded-lg p-1 md:flex',
        header ? 'customer-header-nav' : 'bg-muted text-muted-foreground'
      )}
    >
      {navigation.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/'}
          className={({ isActive }) =>
            cn(
              'focus-visible:ring-ring inline-flex items-center rounded-md px-3 py-1 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none',
              header
                ? cn('customer-header-nav-item', isActive && 'customer-header-nav-item-active')
                : isActive
                  ? 'bg-background text-foreground font-medium shadow-sm'
                  : 'hover:bg-background/60 hover:text-foreground'
            )
          }
        >
          {item.title}
        </NavLink>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <Link
      to="/"
      className="focus-visible:ring-ring flex min-w-0 items-center gap-2 rounded-md font-semibold focus-visible:ring-2 focus-visible:outline-none"
    >
      <img src={appIdentity.iconPath} alt="" className="size-7 shrink-0 rounded-md" />
      <span className="hidden truncate text-sm tracking-tight sm:inline">{appIdentity.name}</span>
    </Link>
  );
}

export function Header({ onMobileSidebarOpen }: { onMobileSidebarOpen: () => void }) {
  const { header } = useCustomerSkin();
  return (
    <header
      data-customer-header={header ? '' : undefined}
      style={header ? { backgroundColor: header.background, color: header.foreground } : undefined}
      className="bg-background/95 supports-[backdrop-filter]:bg-background/60 border-border z-40 flex h-14 shrink-0 items-center border-b backdrop-blur"
    >
      <div className="hidden h-full w-60 shrink-0 items-center px-4 md:flex">
        <Brand />
      </div>
      <div className="flex min-w-0 flex-1 items-center gap-2 px-4 md:gap-3 md:px-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={onMobileSidebarOpen}
          aria-label="Open sidebar"
          className="-ml-1 md:hidden"
        >
          <PanelLeftOpenIcon className="size-5" />
        </Button>
        <div className="md:hidden">
          <Brand />
        </div>
        <TopNav />
        <TenantContextIndicator />
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <NotificationBell />
          <AssistantLauncher />
          <ProfileMenu />
        </div>
      </div>
    </header>
  );
}
